import './AddOrganizationDialog.less';

import { Input, Radio, RadioChangeEvent, Space } from 'antd';
import { Button, FormProps } from 'antd';
import { Form } from 'antd';
import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { OrgEnvironmentType } from '../../enums/organization';
import { fetchCredentials } from '../../services/organizationService';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../app/store';
import { AuthMessageType, CirraServiceType } from '../../enums/auth';
import {
  createOAuthPopup,
  handleOAuthResponse,
} from '../../services/oauthService';
import { buildOAuthUrl } from '../../services/oauthService';
import { fetchAuthParams } from '../../services/oauthService';
import { useNavigate } from 'react-router-dom';
import { OrgCredential } from '../../interfaces/organization';
import {
  setOrganization,
  setAllCredentials,
} from '../../app/Slices/organizationSlice';
import { updateCredential } from '../../app/ActionTypes/organizationActions';
import { Notify } from '../../services/notificationService';
import { AppRoutes } from '../../constants/route';
import { withExponentialBackoff } from '../../utils/exponentialBackoff';

type AddOrganizationDialogProps = {
  service: CirraServiceType;
  defaultOrgType?: OrgEnvironmentType;
  onClose: () => void;
  resetTrigger: number;
  successRedirectTo?: string | null;
  setCredentials?: (credentials: OrgCredential[]) => void;
};

type FormValues = {
  type: OrgEnvironmentType;
  customOrgUrl: string;
  scratchOrgUrl: string;
  developerOrgUrl: string;
};

const HTTP_PREFIX = 'http://';
const HTTPS_PREFIX = 'https://';
const CUSTOM_DOMAIN_SUFFIX = '.my.salesforce.com';
const SCRATCH_DOMAIN_SUFFIX = '.scratch.my.salesforce.com';
const DEVELOPER_DOMAIN_SUFFIX = '.my.salesforce.com';
const SANDBOX_URL = 'https://test.salesforce.com';
const PRODUCTION_URL = 'https://login.salesforce.com';

const ENV_CONFIGS = {
  [OrgEnvironmentType.SCRATCH]: {
    radioLabel: 'Scratch Org',
    domainSuffix: SCRATCH_DOMAIN_SUFFIX,
    urlField: 'scratchOrgUrl',
    urlPreviewField: 'scratchOrgUrlPreview',
    validateBareDomain: null,
    validationMessage: null,
  },

  [OrgEnvironmentType.DEVELOPER]: {
    radioLabel: 'Developer',
    domainSuffix: DEVELOPER_DOMAIN_SUFFIX,
    urlField: 'developerOrgUrl',
    urlPreviewField: 'developerOrgUrlPreview',
    validateBareDomain: (value: string) => {
      // Matches both old (myorg-dev-ed) and new (myorg-dev-ed.develop) dev org domains
      return /-dev-ed(\.develop)?$/.test(value);
    },
    validationMessage:
      'Developer org domains must include "-dev-ed" (e.g. myorg-dev-ed or myorg-dev-ed.develop)',
  },

  [OrgEnvironmentType.CUSTOM]: {
    radioLabel: 'Custom Domain',
    domainSuffix: CUSTOM_DOMAIN_SUFFIX,
    urlField: 'customOrgUrl',
    urlPreviewField: 'customOrgUrlPreview',
    validateBareDomain: null,
    validationMessage: null,
  },

  [OrgEnvironmentType.SANDBOX]: {
    radioLabel: 'Sandbox',
    domainSuffix: null,
    urlField: null,
    urlPreviewField: null,
    validateBareDomain: null,
    validationMessage: null,
  },
  [OrgEnvironmentType.PRODUCTION]: {
    radioLabel: 'Production',
    domainSuffix: null,
    urlField: null,
    urlPreviewField: null,
    validateBareDomain: null,
    validationMessage: null,
  },
};

const getFormInitalState = () => ({
  scratchOrgUrl: '',
  scratchOrgUrlPreview: '',

  developerOrgUrl: '',
  developerOrgUrlPreview: '',

  customOrgUrl: '',
  customOrgUrlPreview: '',
});

const OrgTypeRadio: React.FC<{
  type: OrgEnvironmentType;
  currentType: OrgEnvironmentType;
}> = ({ type, currentType }) => {
  return <Radio value={type}>{ENV_CONFIGS[type].radioLabel}</Radio>;
};

const DEFAULT_ORG_TYPE = OrgEnvironmentType.PRODUCTION;

const AddOrganizationDialog: React.FC<AddOrganizationDialogProps> = ({
  service,
  defaultOrgType = DEFAULT_ORG_TYPE,
  onClose,
  resetTrigger, // TODO: use useImperativeHandle for better pattern of state management
  successRedirectTo = AppRoutes.adminAgent.path,
  setCredentials,
}) => {
  const [form] = Form.useForm();
  const [orgType, setOrgType] = useState<OrgEnvironmentType>(defaultOrgType);
  const [formState, setFormState] = useState(getFormInitalState());
  const [authState, setAuthState] = useState({
    loading: false,
    postMessage: { type: '' as AuthMessageType | '', message: '' },
  });
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useEffect(() => {
    setOrgType(defaultOrgType);
    form.resetFields();
    setAuthState((prev) => ({
      ...prev,
      loading: false,
      postMessage: { type: '', message: '' },
    }));
    setFormState(getFormInitalState());
  }, [resetTrigger]);

  const getOrgBaseUrl = (type: OrgEnvironmentType, customOrgUrl: string) => {
    switch (type) {
      case OrgEnvironmentType.PRODUCTION:
        return PRODUCTION_URL;
      case OrgEnvironmentType.SANDBOX:
        return SANDBOX_URL;
      default:
        return customOrgUrl;
    }
  };

  const selectOrg = async (
    userId: string,
    organizationId: string,
    pathAtOAuthStart: string
  ) => {
    // The dialog is closed by the caller as soon as OAuth succeeds, so this
    // runs in the background. Don't touch authState here — it would update an
    // unmounted component (the Walkthrough conditionally renders the dialog).
    try {
      // Use exponential backoff to wait for credential to be created and become available
      const credentials = await withExponentialBackoff(
        async () => {
          const creds = await fetchCredentials();
          const foundCred = creds.find(
            (cred) =>
              cred.user_id === userId && cred.organization_id === organizationId
          );

          if (!foundCred) {
            throw new Error(`Credential for user ${userId} not yet available`);
          }

          return creds;
        },
        {
          initialDelay: 500, // Start with 500ms since we now have fresh tokens
          maxDelay: 5000, // Max 5 seconds between retries
          maxRetries: 6, // Try up to 6 times
          multiplier: 1.5, // Moderate backoff
          jitter: true, // Add randomness
        },
        (error, attempt) => {
          // Only retry if credential not found, not for other errors
          return (
            error instanceof Error &&
            error.message.includes('not yet available')
          );
        }
      );

      // Update Redux store with all credentials
      dispatch(setAllCredentials(credentials));

      if (setCredentials) {
        setCredentials(credentials);
      }

      const foundCredential = credentials.find(
        (cred) =>
          cred.user_id === userId && cred.organization_id === organizationId
      );

      if (!foundCredential) {
        throw new Error('Unable to verify Salesforce organization!');
      }

      // If the new credential is not already linked to a shared connection, check if one exists
      let credentialToSelect: OrgCredential = foundCredential;
      if (!foundCredential.source_id && foundCredential.organization_id) {
        const sharedConnection = credentials.find(
          (c) =>
            c.is_shared &&
            c.organization_id === foundCredential.organization_id &&
            c.id !== foundCredential.id
        );
        if (sharedConnection) {
          try {
            await dispatch(
              updateCredential({
                id: foundCredential.id,
                source_id: sharedConnection.id,
              })
            ).unwrap();
            // Create new object to avoid mutating Redux state
            credentialToSelect = {
              ...foundCredential,
              source_id: sharedConnection.id,
            };
          } catch (e) {
            // Non-critical: backend may have already linked it
            console.debug(
              'Failed to link new credential to shared connection:',
              e
            );
          }
        }
      }

      dispatch(setOrganization(credentialToSelect));

      Notify.success('Salesforce organization selected successfully!');
      // Only navigate if the user hasn't moved since OAuth started. Because
      // the dialog closes immediately on OAuth success, selectOrg can finish
      // seconds later; firing navigate unconditionally would hijack the
      // user's manual navigation.
      if (successRedirectTo && window.location.pathname === pathAtOAuthStart) {
        navigate(successRedirectTo);
      }
    } catch (error) {
      console.error('Error in selectOrg:', error);
      Notify.error(
        error instanceof Error
          ? error.message
          : 'Failed to select Salesforce organization. Please refresh the page to see your organization.',
        0
      );
    }
  };

  const onSubmit: FormProps<FormValues>['onFinish'] = async (values) => {
    let inputUrl =
      values.scratchOrgUrl ||
      values.customOrgUrl ||
      values.developerOrgUrl ||
      '';
    setAuthState((prev) => ({ ...prev, loading: true }));
    try {
      const domainSuffix = ENV_CONFIGS[values.type].domainSuffix;
      if (domainSuffix) {
        inputUrl = `${HTTPS_PREFIX}${inputUrl}${domainSuffix}`;
      }
      const authParams = await fetchAuthParams({
        orgInfo: null,
        envType: values.type,
        service,
      });
      const url = getOrgBaseUrl(values.type, inputUrl);
      const oauthUrl = buildOAuthUrl({ authParams, url });
      const pathAtOAuthStart = window.location.pathname;

      createOAuthPopup(
        oauthUrl,
        (event: MessageEvent) =>
          handleOAuthResponse(
            event,
            async (result) => {
              // Close the dialog as soon as OAuth succeeds. selectOrg can take
              // several seconds to complete due to exponential-backoff retries
              // waiting for the new credential to become available on the backend;
              // keeping the dialog open that whole time (and risking it getting
              // stuck if selectOrg fails) is a bad experience. Errors from
              // selectOrg still surface via Notify.error.
              onClose();

              await selectOrg(
                result.userId,
                result.organizationId,
                pathAtOAuthStart
              );
            },
            (error) => {
              setAuthState((prev) => ({
                ...prev,
                loading: false,
                postMessage: {
                  type: AuthMessageType.ERROR,
                  message: error,
                },
              }));
            }
          ),
        () => {
          if (authState.postMessage.type === '') {
            setAuthState((prev) => ({ ...prev, loading: false }));
          }
        }
      );
    } catch (error) {
      setAuthState((prev) => ({ ...prev, loading: false }));
      Notify.error((error as Error).message || 'Failed to add organization');
    }
  };

  const handleUrlChange = (
    type: OrgEnvironmentType,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const rawInputValue = e.target.value;
    const envConfigs = ENV_CONFIGS[type];
    const suffix = envConfigs.domainSuffix || '';
    const domain = getSanitizedDomain(rawInputValue, suffix);
    const previewUrl = `${HTTPS_PREFIX}${domain}${suffix}`;
    const urlField = envConfigs.urlField || '';
    const urlPreviewField = envConfigs.urlPreviewField || '';

    // Update form state
    setFormState((prev) => ({
      ...prev,
      [urlField]: domain,
      [urlPreviewField]: previewUrl,
    }));
    form.setFieldsValue({
      [urlField]: domain,
    });
  };

  const getSanitizedDomain = (url: string, suffix: string): string => {
    return url
      .replace(/\s+/g, '')
      .replace(new RegExp(`^${HTTPS_PREFIX.replace('.', '\\.')}`), '')
      .replace(new RegExp(`^${HTTP_PREFIX.replace('.', '\\.')}`), '')
      .replace(new RegExp(`${suffix.replace('.', '\\.')}$`), '');
  };

  const onChange = useCallback((e: RadioChangeEvent) => {
    setOrgType(e.target.value);
  }, []);

  const renderEnvCheckboxWithDomainSuffix = useCallback(
    (
      _elementEnvType: OrgEnvironmentType,
      _currentOrgType: OrgEnvironmentType
    ) => {
      const _envConfigs = ENV_CONFIGS[_elementEnvType];
      const inputtedDomainValue =
        (_envConfigs.urlField
          ? formState[_envConfigs.urlField as keyof typeof formState]
          : '') || '';
      const previewUrlValue =
        (_envConfigs.urlPreviewField
          ? formState[_envConfigs.urlPreviewField as keyof typeof formState]
          : '') || '';

      return (
        <div className="radio-group-item">
          <OrgTypeRadio type={_elementEnvType} currentType={_currentOrgType} />
          {_currentOrgType === _elementEnvType && (
            <Form.Item>
              <Form.Item
                name={_envConfigs.urlField || ''}
                rules={[
                  {
                    required: true,
                    message: `Please input a Salesforce ${_.capitalize(_elementEnvType)} Org Url!`,
                  },
                  {
                    validator: (__, value) => {
                      if (!value || !_envConfigs.validateBareDomain) {
                        return Promise.resolve();
                      }
                      const isValid = _envConfigs.validateBareDomain(value);
                      if (!isValid) {
                        return Promise.reject(
                          new Error(
                            _envConfigs.validationMessage ||
                              'Please enter a valid domain'
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  addonBefore={HTTPS_PREFIX}
                  addonAfter={_envConfigs.domainSuffix}
                  placeholder="Org Domain"
                  disabled={authState.loading}
                  value={inputtedDomainValue}
                  onChange={(e) => handleUrlChange(_elementEnvType, e)}
                />
              </Form.Item>

              {inputtedDomainValue.trim() && (
                <p className="preview-url">{previewUrlValue}</p>
              )}
            </Form.Item>
          )}
        </div>
      );
    },
    [formState, authState, handleUrlChange]
  );

  return (
    <div id="add-organization">
      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        initialValues={{ type: defaultOrgType, customOrgUrl: '' }}
        data-hs-do-not-collect="true"
      >
        <Form.Item name="type" label="Salesforce Org Type">
          <Radio.Group
            onChange={onChange}
            value={orgType}
            disabled={authState.loading}
          >
            <Space direction="vertical">
              <OrgTypeRadio
                type={OrgEnvironmentType.PRODUCTION}
                currentType={orgType}
              />
              <OrgTypeRadio
                type={OrgEnvironmentType.SANDBOX}
                currentType={orgType}
              />
              {renderEnvCheckboxWithDomainSuffix(
                OrgEnvironmentType.DEVELOPER,
                orgType
              )}

              {renderEnvCheckboxWithDomainSuffix(
                OrgEnvironmentType.SCRATCH,
                orgType
              )}

              {renderEnvCheckboxWithDomainSuffix(
                OrgEnvironmentType.CUSTOM,
                orgType
              )}
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item>
          <div className="footer-wrapper">
            <div className="message-wrapper">
              {authState.postMessage.type === AuthMessageType.ERROR && (
                <div className="error-message">
                  Error: {authState.postMessage.message}
                </div>
              )}
            </div>
            <div className="button-wrapper">
              <Button
                type="primary"
                htmlType="submit"
                loading={authState.loading}
              >
                Submit
              </Button>
            </div>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AddOrganizationDialog;
